// Rate lookup tab: look up the saved combined rate for an address and save
// a corrected one. Lookup + save only — the backend serves no jurisdiction
// list, so this page shows the looked-up record, never a saved list.
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useState } from "react";
import {
  lookupTaxJurisdiction,
  saveTaxJurisdiction,
  type TaxLookupResult,
} from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function rateWithBp(rate_bp: number): string {
  return `${(rate_bp / 100).toFixed(2)}% (${rate_bp} bp)`;
}

function toRateBp(percent: string): number | null {
  const trimmed = percent.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return Math.round(value * 100);
}

export function LookupTab() {
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [result, setResult] = useState<TaxLookupResult | null>(null);
  const [lookedUp, setLookedUp] = useState(false);
  const [looking, setLooking] = useState(false);

  const [combined, setCombined] = useState("");
  const [stateRate, setStateRate] = useState("");
  const [countyRate, setCountyRate] = useState("");
  const [cityRate, setCityRate] = useState("");
  const [specialRate, setSpecialRate] = useState("");
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const runLookup = useCallback(async () => {
    const code = country.trim().toUpperCase();
    if (!code) {
      setError("Enter a country code first (e.g. US, GB, AU).");
      return;
    }
    setLooking(true);
    setError(null);
    setNotice(null);
    try {
      const found = await lookupTaxJurisdiction({
        country: code,
        ...(state.trim() ? { state: state.trim() } : {}),
        ...(postal.trim() ? { postal_code: postal.trim() } : {}),
      });
      setResult(found);
      setLookedUp(true);
      if (found) {
        setCombined(String(found.combined_rate_bp / 100));
        setStateRate(String(found.state_rate_bp / 100));
        setCountyRate(String(found.county_rate_bp / 100));
        setCityRate(String(found.city_rate_bp / 100));
        setSpecialRate(String(found.special_rate_bp / 100));
      }
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setLooking(false);
    }
  }, [country, state, postal]);

  const submitLookup = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void runLookup();
  };

  const submitSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const code = country.trim().toUpperCase();
    const parts = {
      combined_rate_bp: toRateBp(combined),
      state_rate_bp: toRateBp(stateRate),
      county_rate_bp: toRateBp(countyRate),
      city_rate_bp: toRateBp(cityRate),
      special_rate_bp: toRateBp(specialRate),
    };
    if (!code) {
      setError("Enter a country code first.");
      return;
    }
    const bad = Object.entries(parts).find(([, value]) => value === null);
    if (bad) {
      setError("Every rate part must be a percent from 0 to 100.");
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await saveTaxJurisdiction({
        country: code,
        ...(state.trim() ? { state: state.trim() } : {}),
        ...(postal.trim() ? { postal_code: postal.trim() } : {}),
        combined_rate_bp: parts.combined_rate_bp as number,
        state_rate_bp: parts.state_rate_bp as number,
        county_rate_bp: parts.county_rate_bp as number,
        city_rate_bp: parts.city_rate_bp as number,
        special_rate_bp: parts.special_rate_bp as number,
        source: "manual",
      });
      setNotice(
        `Saved the ${code} rate${saved?.id ? ` (${saved.id.slice(0, 8)}…)` : ""} at ${saved ? rateWithBp(saved.combined_rate_bp) : "the entered rate"}.`,
      );
      setResult(saved);
      setLookedUp(true);
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-4">
      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void runLookup()}
              disabled={looking}
            >
              Retry lookup
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <form
        onSubmit={submitLookup}
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <label htmlFor="tj-country" className="grid gap-1 text-xs font-medium">
          Country code
          <Input
            id="tj-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="US"
            maxLength={2}
            disabled={looking}
          />
        </label>
        <label htmlFor="tj-state" className="grid gap-1 text-xs font-medium">
          State (optional)
          <Input
            id="tj-state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            placeholder="CA"
            disabled={looking}
          />
        </label>
        <label htmlFor="tj-postal" className="grid gap-1 text-xs font-medium">
          Postcode (optional)
          <Input
            id="tj-postal"
            value={postal}
            onChange={(e) => setPostal(e.target.value)}
            placeholder="94107"
            disabled={looking}
          />
        </label>
        <Button type="submit" variant="secondary" disabled={looking}>
          {looking ? "Looking up…" : "Look up rate"}
        </Button>
      </form>

      {looking ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Looking up the saved rate…
        </div>
      ) : !error && !lookedUp ? (
        <EmptyState
          title="No lookup yet"
          reason="Enter an address above — the saved combined rate for that place shows here, if one exists."
        />
      ) : lookedUp && (!result || Array.isArray(result)) ? (
        <EmptyState
          title="No saved rate for that address"
          reason="Nothing covers this place yet — save the correct split below and future lookups find it."
        />
      ) : result && !Array.isArray(result) ? (
        <section
          aria-label="Looked-up rate"
          className="surface overflow-hidden rounded-2xl"
          data-testid="tax-lookup-result"
        >
          <h3 className="bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]">
            {result.country}
            {result.state ? ` · ${result.state}` : ""}
            {result.postal_code ? ` · ${result.postal_code}` : ""} ·{" "}
            {result.source}
          </h3>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className="font-semibold">Combined rate</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">
                  {rateWithBp(result.combined_rate_bp)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>State part</TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateWithBp(result.state_rate_bp)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>County part</TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateWithBp(result.county_rate_bp)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>City part</TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateWithBp(result.city_rate_bp)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Special part</TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateWithBp(result.special_rate_bp)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </section>
      ) : null}

      <form
        onSubmit={submitSave}
        className="surface grid gap-3 rounded-2xl p-5"
      >
        <h3 className="text-sm font-semibold">
          Save the rate for this address
        </h3>
        <div className="flex flex-wrap items-end gap-3">
          <label
            htmlFor="tj-combined"
            className="grid gap-1 text-xs font-medium"
          >
            Combined (%)
            <Input
              id="tj-combined"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={combined}
              onChange={(e) => setCombined(e.target.value)}
              disabled={saving}
            />
          </label>
          <label
            htmlFor="tj-state-rate"
            className="grid gap-1 text-xs font-medium"
          >
            State (%)
            <Input
              id="tj-state-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={stateRate}
              onChange={(e) => setStateRate(e.target.value)}
              disabled={saving}
            />
          </label>
          <label
            htmlFor="tj-county-rate"
            className="grid gap-1 text-xs font-medium"
          >
            County (%)
            <Input
              id="tj-county-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={countyRate}
              onChange={(e) => setCountyRate(e.target.value)}
              disabled={saving}
            />
          </label>
          <label
            htmlFor="tj-city-rate"
            className="grid gap-1 text-xs font-medium"
          >
            City (%)
            <Input
              id="tj-city-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={cityRate}
              onChange={(e) => setCityRate(e.target.value)}
              disabled={saving}
            />
          </label>
          <label
            htmlFor="tj-special-rate"
            className="grid gap-1 text-xs font-medium"
          >
            Special (%)
            <Input
              id="tj-special-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={specialRate}
              onChange={(e) => setSpecialRate(e.target.value)}
              disabled={saving}
            />
          </label>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save rate"}
          </Button>
        </div>
        <p className="text-xs text-[var(--muted-foreground)]">
          Saved under the country, state, and postcode above. Looking the same
          address up later finds it.
        </p>
      </form>
    </div>
  );
}
